// @ts-strict-ignore
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { flattenOnPropertyReducer } from '../../functions/utilities/array-utility';
import { EditorTree } from './editor-tree';
import { EditorTreeBoardModel } from './editor-tree-board-model';
import { EditorTreeNode } from './editor-tree-node';
import {
  EDITOR_TREE_INITIAL,
  EditorTreeType,
  NavigationTreeData,
  VIEW_PARAMETERS,
  VIEW_PARAMETERS_DEFAULT,
  ViewParameters,
} from './editor-tree.constants';

@Injectable({
  providedIn: 'root',
})
export class EditorTreeService implements OnDestroy {
  private _activeSelectedNodes: BehaviorSubject<EditorTreeNode[]>;
  private _breadcrumbs: BehaviorSubject<(EditorTree | EditorTreeNode)[]>;
  private _flatNodes: { [key in EditorTreeType]: EditorTreeNode[] };
  private _navigationTreeData: BehaviorSubject<NavigationTreeData>;
  private _selectedNodes: { [key in EditorTreeType]: EditorTreeNode[] };
  private _subscriptions: Subscription = new Subscription();
  private _trees: { [key in EditorTreeType]: EditorTree };
  private _viewParameters: ViewParameters;

  get activeSelectedNodes() {
    return this._activeSelectedNodes;
  }

  get activeTreeType() {
    return this._viewParameters?.activeTreeType;
  }

  get breadcrumbs() {
    return this._breadcrumbs;
  }

  get navigationTreeData() {
    return this._navigationTreeData;
  }

  get trees() {
    return this._trees;
  }

  constructor() {
    this._activeSelectedNodes = new BehaviorSubject([]);
    this._trees = this._setInitialValues(EDITOR_TREE_INITIAL);
    this._navigationTreeData = new BehaviorSubject({
      activeTreeType: undefined,
      availableTreeTypes: [],
      tree: EDITOR_TREE_INITIAL,
      trees: this._trees,
      view: undefined,
    });
    this._breadcrumbs = new BehaviorSubject([]);
    this._flatNodes = this._setInitialValues([]);
    this._selectedNodes = this._setInitialValues([]);
    this._subscriptions.add(
      this._activeSelectedNodes.subscribe((nodes) => {
        this._selectedNodes[this.activeTreeType] = nodes;
      })
    );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  deselectAll(): void {
    this._activeSelectedNodes.next([]);
    this._breadcrumbs.next([this._trees[this.activeTreeType]]);
  }

  getAllChildrenForNode(node: EditorTreeNode): EditorTreeNode[] {
    return node.children.reduce(flattenOnPropertyReducer('children'), []);
  }

  getIsNodeSelected(nodeId: string): boolean {
    return this._activeSelectedNodes.value.some((n) => n.id === nodeId);
  }

  selectActiveNodes(nodes: EditorTreeNode[]) {
    this._activeSelectedNodes.next(nodes);
  }

  selectTree(treeType: EditorTreeType) {
    this._viewParameters.activeTreeType = treeType;
    this._navigationTreeData.next({ tree: this._trees[treeType], trees: this._trees, ...this._viewParameters });
    this._activeSelectedNodes.next(
      !!this._selectedNodes[this.activeTreeType].length ? this._selectedNodes[this.activeTreeType] : []
    );
    this._buildAndEmitNewBreadCrumbs(treeType);
    return this._trees[treeType];
  }

  setAvailableTreeTypes(treeTitles: { [key in EditorTreeType]?: string }, availableTree: EditorTreeType[]) {
    this._viewParameters.treeTitles = treeTitles;
    this._viewParameters.availableTreeTypes = availableTree;
    this.navigationTreeData.next({
      tree: this._trees[this._viewParameters.activeTreeType],
      trees: this._trees,
      ...this._viewParameters,
    });
  }

  setEditorTree(editorTree: EditorTree, treeType: EditorTreeType = EditorTreeType.ActivityHierarchy): void {
    if (this._trees[treeType].id !== editorTree.id) {
      this._activeSelectedNodes.next([]);
      this._selectedNodes[treeType] = [];
    } else {
      if (this._activeSelectedNodes.value?.[0]?.id !== this._selectedNodes[this.activeTreeType]?.[0]?.id) {
        this._activeSelectedNodes.next(
          !!this._selectedNodes[this.activeTreeType].length ? this._selectedNodes[this.activeTreeType] : []
        );
      }
    }
    if (editorTree) {
      this._trees[treeType] = editorTree;
    }
    this._flatNodes[treeType] = this._trees[treeType].children.reduce(flattenOnPropertyReducer('children'), []);
    this._navigationTreeData.next({
      tree: this._trees[treeType],
      trees: this._trees,
      ...this._viewParameters,
    });
    this._buildAndEmitNewBreadCrumbs(treeType);
  }

  setInventoryOrEconomicsTree(
    treeType:
      | EditorTreeType.Economics
      | EditorTreeType.Inventory
      | EditorTreeType.GPC
      | EditorTreeType.ActivityHierarchy
      | string,
    tree: EditorTree | false,
    treeTitles?: { [key in EditorTreeType]?: string },
    availableTreeTypes?: EditorTreeType[]
  ) {
    if (tree) {
      this._trees[treeType] = tree;
    }
    this._viewParameters.activeTreeType = treeType as EditorTreeType;
    if (treeTitles) {
      this._viewParameters.treeTitles = treeTitles;
    }
    if (availableTreeTypes) {
      this._viewParameters.availableTreeTypes = availableTreeTypes;
    }
    this._navigationTreeData.next({
      tree: this._trees[treeType],
      trees: this._trees,
      ...this._viewParameters,
    });
  }

  setViewParameters(board: EditorTreeBoardModel, url: string) {
    let view: string;
    [view] = url.split('/').slice(-1);
    if (view.includes('?')) {
      [view] = view.split('?').slice(0, 1);
    }
    const parameters = {
      ...VIEW_PARAMETERS_DEFAULT,
      ...VIEW_PARAMETERS[view],
    };

    if (!board.supplyChildNodes?.length) {
      parameters.availableTreeTypes = parameters.availableTreeTypes.filter((t) => t !== EditorTreeType.Resources);
    }

    const activeTreeType = parameters.availableTreeTypes.some((t) => t === this.activeTreeType)
      ? this.activeTreeType
      : parameters.availableTreeTypes[0];

    this._viewParameters = { ...parameters, activeTreeType, view };
    this._navigationTreeData.next({
      tree: this._trees[this.activeTreeType],
      trees: this._trees,
      ...this._viewParameters,
    });
  }

  toggleSelectNode(withCtrlKey: boolean, node: EditorTreeNode): void {
    const nodeAlreadySelected = this.getIsNodeSelected(node.id);
    if (nodeAlreadySelected) {
      if (withCtrlKey) {
        this._activeSelectedNodes.next(this._activeSelectedNodes.value.filter((n) => n.id !== node.id));
      } else {
        this._activeSelectedNodes.next([node]);
        this._breadcrumbs.next(this._buildBreadCrumbs([], node));
      }
    } else {
      if (withCtrlKey) {
        const alreadySelectedAsChild = this._getIsSelectedAsChild(node);
        if (alreadySelectedAsChild) {
          return;
        } else {
          const newChildren = this.getAllChildrenForNode(node);
          const newSelected = this._activeSelectedNodes.value
            .filter((n) => !newChildren.some((c) => n.id === c.id))
            .concat([node]);
          this._activeSelectedNodes.next(newSelected);
        }
      } else {
        this._activeSelectedNodes.next([node]);
        this._breadcrumbs.next(this._buildBreadCrumbs([], node));
      }
    }
  }

  private _buildBreadCrumbs(
    arr: Array<EditorTree | EditorTreeNode>,
    node: EditorTreeNode
  ): Array<EditorTree | EditorTreeNode> {
    arr.unshift(node);
    if (node?.parentId) {
      const tree = this._trees[this.activeTreeType];
      const nodes = this._flatNodes[this.activeTreeType];
      if (node.parentId === tree.id) {
        arr.unshift(tree);
      } else {
        arr.unshift(
          ...this._buildBreadCrumbs(
            [],
            nodes?.find((n) => n.id === node.parentId)
          )
        );
      }
    }
    return arr;
  }

  private _buildAndEmitNewBreadCrumbs = (treeType: EditorTreeType) => {
    if (!this._viewParameters.multiSelect && this._activeSelectedNodes.value.length) {
      this._breadcrumbs.next(this._buildBreadCrumbs([], this._activeSelectedNodes.value[0]));
    } else {
      this._breadcrumbs.next([this._trees[treeType]]);
    }
  };

  private _getIsSelectedAsChild(node: EditorTreeNode): boolean {
    const selectedNodes = this._activeSelectedNodes.getValue();
    const allSelectedNodesChildren = selectedNodes.reduce(flattenOnPropertyReducer('children'), []);
    return allSelectedNodesChildren.some((n) => n.id === node.id);
  }

  private _setInitialValues<T>(value: T) {
    return <{ [key in EditorTreeType]: T }>Object.values(EditorTreeType).reduce((trees, tree) => {
      trees[tree] = value;
      return trees;
    }, {});
  }
}
